/* deboer — Gift with purchase (GWP) engine.

   Replaces the third-party gift-box app. Read snippets/deboer-gift.liquid for
   the split of responsibility; the short version is that a native Shopify
   automatic "Buy X get Y" discount owns the price and this file owns the
   line item. It never writes a price, only adds/removes/swaps a line and
   re-renders the sections Shopify hands back.

   The one rule that keeps it honest: if the gift line comes back at anything
   other than zero, the discount is not doing its job — pull the line and hide
   the promo rather than let a "free" backpack reach checkout with a price on
   it. Failing closed is the whole reason this is worth owning in-theme.

   Depends on Dawn's pubsub.js (subscribe/PUB_SUB_EVENTS) and global.js
   (fetchConfig), both loaded ahead of this file in theme.liquid.
*/
(function () {
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function cfg() {
    return typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  }

  class DeboerGiftEngine extends HTMLElement {
    connectedCallback() {
      this.active = this.dataset.active === 'true';
      this.campaign = this.dataset.campaign || 'gwp';
      this.defaultVariant = parseInt(this.dataset.defaultVariant, 10) || 0;
      // Optional women's gift (Iceman → hooded vest, Oct 2026). A qualifying
      // line from a womens- product earns it instead of the main gift.
      this.giftProduct = parseInt(this.dataset.giftProduct, 10) || 0;
      this.giftProductWomen = parseInt(this.dataset.giftProductWomen, 10) || 0;
      this.defaultVariantWomen = parseInt(this.dataset.defaultVariantWomen, 10) || 0;
      this.womenIds = (this.dataset.womenIds || '').split(',').map(Number).filter(Boolean);
      // Empty unless the whole qualifying collection fit in the id list; an
      // incomplete list would wrongly disqualify the products it left out.
      this.ids =
        this.dataset.qualifyIdsComplete === 'true' && this.dataset.qualifyIds
          ? this.dataset.qualifyIds.split(',').map(Number).filter(Boolean)
          : [];
      this.busy = false;
      this.rerun = false;
      // Set when a gift line comes back priced: stop trying for this pageview.
      this.broken = false;
      this.addTried = false;

      this.keyDeclined = 'deboer:gwp:' + this.campaign + ':declined';
      this.keyColour = 'deboer:gwp:' + this.campaign + ':colour';

      // A shopper who removes the gift has said no. Never fight them by
      // re-adding it on the next cart change — that is the single most
      // irritating behaviour of the app we are replacing.
      document.addEventListener('click', this.onClick.bind(this), true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Cold load: clean up a stale gift (campaign ended, or the shopper
      // emptied the cart in another tab) and honour a cart that already
      // qualifies. Deferred so the first paint is never blocked.
      if ('requestIdleCallback' in window) requestIdleCallback(() => this.reconcile());
      else setTimeout(() => this.reconcile(), 400);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /* ---------------------------------------------------------------- events */

    onClick(evt) {
      var decline = evt.target.closest && evt.target.closest('[data-gwp-decline]');
      if (decline) {
        this.store(this.keyDeclined, '1');
        this.track('gwp_declined');
        return; // let Dawn's cart-remove-button do the actual removal
      }
      var claim = evt.target.closest && evt.target.closest('[data-gwp-claim]');
      if (claim) {
        evt.preventDefault();
        this.forget(this.keyDeclined);
        this.track('gwp_reclaimed');
        this.reconcile();
        return;
      }
      var swatch = evt.target.closest && evt.target.closest('[data-gwp-swatch]');
      if (swatch && !swatch.disabled) {
        evt.preventDefault();
        this.swap(parseInt(swatch.dataset.gwpSwatch, 10));
      }
    }

    // The "claim it back" prompt is the only piece Liquid can't decide, since
    // declining is remembered client-side.
    showClaim(on) {
      document.querySelectorAll('.dgift-claim').forEach((el) => {
        el.hidden = !on;
      });
    }

    onCartUpdate(event) {
      // Ignore the echo of our own render (published as 'cart-items', see
      // render()); reconciling on it would loop.
      if (!event || event.source === 'deboer-gift' || this.rendering) return;
      // A real cart change earns a fresh attempt — the shopper may have just
      // added the wetsuit that unlocks a gift we previously couldn't place.
      this.addTried = false;

      // A qualifying add is identified by the product id Dawn reports against
      // the [data-gwp-product] markers the PDP strip and card badges render.
      var added = null;
      if (event.cartData) added = event.cartData.product_id || (event.cartData.items || [])[0]?.product_id;
      if (added && document.querySelector('[data-gwp-product="' + added + '"]')) this.hintQualified = true;

      // cart-items renders its sections before publishing, but wait a frame so
      // we are never reading a half-swapped .js-contents.
      return new Promise((resolve) => {
        requestAnimationFrame(() => this.reconcile().then(resolve, resolve));
      });
    }

    /* ----------------------------------------------------------------- state */

    // Judged against the cart we just fetched wherever possible — the id list
    // is exact and needs no markup on the page. The server-rendered flags are
    // the fallback for a collection too large to enumerate.
    qualified(cart) {
      if (cart && this.ids && this.ids.length) {
        return (cart.items || []).some((i) => {
          if (i.properties && i.properties._gwp) return false; // the gift itself never qualifies
          return this.ids.indexOf(i.product_id) !== -1;
        });
      }
      var el = document.querySelector('.dgift-state[data-gwp-qualified]');
      if (el) return el.dataset.gwpQualified === 'true';
      if (this.hintQualified) return true;
      return this.dataset.qualified === 'true';
    }

    // Which gift this cart earns: the women's one when the first qualifying
    // line is a women's product, otherwise the main gift.
    wanted(cart) {
      var main = { product: this.giftProduct, variant: this.defaultVariant };
      if (!this.giftProductWomen || !cart) return main;
      var first = (cart.items || []).find((i) => {
        if (i.properties && i.properties._gwp) return false;
        return !this.ids.length || this.ids.indexOf(i.product_id) !== -1;
      });
      if (first && this.womenIds.indexOf(first.product_id) !== -1) {
        return { product: this.giftProductWomen, variant: this.defaultVariantWomen };
      }
      return main;
    }

    // The shopper's size/colour pick, per gift product — a men's size id is
    // meaningless for the women's gift.
    colourKey(productId) {
      return this.keyColour + (productId ? ':' + productId : '');
    }

    async reconcile() {
      if (this.busy) {
        this.rerun = true;
        return;
      }
      this.busy = true;
      try {
        var cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        var mine = [];
        var stale = [];
        (cart.items || []).forEach((item) => {
          var tag = item.properties && item.properties._gwp;
          if (!tag) return;
          if (tag === this.campaign) mine.push(item);
          else stale.push(item);
        });

        // Gifts from a finished campaign never linger in a cart.
        for (var i = 0; i < stale.length; i++) await this.setQty(stale[i].key, 0);

        if (!this.active || this.broken) {
          for (var j = 0; j < mine.length; j++) await this.setQty(mine[j].key, 0);
          return;
        }

        var gift = mine[0];

        // Duplicates can only come from a double-fire; collapse to one.
        for (var k = 1; k < mine.length; k++) await this.setQty(mine[k].key, 0);

        if (!this.qualified(cart)) {
          this.showClaim(false);
          if (gift) {
            await this.setQty(gift.key, 0);
            this.track('gwp_removed');
          }
          return;
        }

        var want = this.wanted(cart);

        // Wrong gift for the cart (the women's suit was swapped for a men's,
        // or vice versa): trade it for the right one. Not a decline.
        if (gift && want.product && gift.product_id !== want.product) {
          await this.setQty(gift.key, 0);
          gift = null;
          this.addTried = false;
        }

        if (!gift) {
          if (this.read(this.keyDeclined)) {
            this.showClaim(true);
            return;
          }
          // addTried stops a sold-out gift from retrying forever: the rerun
          // below comes straight back here with still no gift line.
          if (this.addTried) return;
          this.addTried = true;
          this.want = want;
          await this.add();
          // Re-enter once so the line we just created gets price-checked. The
          // add response can't tell us — only a fresh cart read can — and an
          // unchecked add is exactly how a priced "gift" reaches checkout.
          this.rerun = true;
          return;
        }
        this.showClaim(false);

        // The discount failed to apply. Do not let a priced gift ride to
        // checkout — remove it and stand the promo down for this pageview.
        if (gift.original_line_price > 0 && gift.final_line_price !== 0) {
          await this.setQty(gift.key, 0);
          this.failClosed(gift);
          return;
        }

        if (gift.quantity !== 1) await this.setQty(gift.key, 1);
      } catch (e) {
        // A cart the shopper can still check out with beats a loud failure.
        if (window.console) console.warn('[deboer-gift]', e);
      } finally {
        this.busy = false;
        if (this.rerun) {
          this.rerun = false;
          this.reconcile();
        }
      }
    }

    /* --------------------------------------------------------------- actions */

    async add(variantId) {
      var want = this.want || { product: this.giftProduct, variant: this.defaultVariant };
      var fallback = want.variant || this.defaultVariant;
      var id = variantId || parseInt(this.read(this.colourKey(want.product)), 10) || fallback;
      if (!id) return;
      var body = {
        items: [{ id: id, quantity: 1, properties: { _gwp: this.campaign } }],
      };
      var res = await this.post(ROOT + 'cart/add.js', body, true);
      if (res && res.status) {
        // Sold out between render and click (staging is a good place to hit
        // this). Fall back to the campaign default once, then give up.
        if (id !== fallback && fallback) {
          this.forget(this.colourKey(want.product));
          return this.add(fallback);
        }
        return;
      }
      // Marks the render that followed an add so the "Free" tag sweeps once.
      // Cleared straight away; an unrelated cart re-render must not replay it.
      var row = document.querySelector('.dgift');
      if (row) {
        row.classList.add('dgift--new');
        setTimeout(function () {
          row.classList.remove('dgift--new');
        }, 1200);
      }
      this.track('gwp_added', { variant_id: id });
      this.announce('gift_added');
    }

    async setQty(key, qty) {
      return this.post(ROOT + 'cart/change.js', { id: key, quantity: qty }, true);
    }

    // /cart/change.js cannot move a line to another variant, so a colour swap
    // is remove-then-add. Only the add re-renders, so the row never blinks.
    async swap(variantId) {
      if (this.busy || !variantId) return;
      this.busy = true;
      var row = document.querySelector('.dgift');
      if (row) row.classList.add('dgift--busy');
      try {
        var cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        var gift = (cart.items || []).find((i) => i.properties && i.properties._gwp === this.campaign);
        if (gift && gift.variant_id === variantId) return;
        // Swatches only ever offer variants of the gift already in the bag.
        var pid = gift ? gift.product_id : this.giftProduct;
        this.store(this.colourKey(pid), String(variantId));
        this.want = { product: pid, variant: pid === this.giftProductWomen ? this.defaultVariantWomen : this.defaultVariant };
        if (gift) await this.post(ROOT + 'cart/change.js', { id: gift.key, quantity: 0 }, false);
        await this.add(variantId);
        this.track('gwp_colour_changed', { variant_id: variantId });
      } catch (e) {
        if (window.console) console.warn('[deboer-gift]', e);
      } finally {
        this.busy = false;
        if (row) row.classList.remove('dgift--busy');
      }
    }

    failClosed(gift) {
      this.broken = true;
      document.querySelectorAll('.dgift, .dgift-strip, .dgift-badge, .dgift-teaser').forEach((el) => {
        el.hidden = true;
      });
      this.track('gwp_discount_missing', { variant_id: gift.variant_id, price: gift.final_line_price });
      // Ops beacon: a live campaign whose discount stopped applying is a
      // revenue bug someone needs to see the same day, not next month.
      if (window.deboer && window.deboer.beacon) {
        window.deboer.beacon('/beacon/error', {
          kind: 'gwp_discount_missing',
          campaign: this.campaign,
          price: gift.final_line_price,
        });
      }
    }

    /* -------------------------------------------------------------- plumbing */

    async post(url, body, render) {
      var sections = render ? this.sections() : [];
      if (sections.length) {
        body.sections = sections.map((s) => s.section);
        body.sections_url = window.location.pathname;
      }
      var res = await fetch(url, Object.assign({}, cfg(), { body: JSON.stringify(body) }));
      var state = await res.json();
      if (state && state.status) return state; // Shopify error payload
      if (render) this.render(state, sections);
      return state;
    }

    // Mirrors Dawn's CartItems.getSectionsToRender(), but only for the pieces
    // actually on this page — the gift is added from the PDP as often as the
    // cart, and the icon bubble has to move either way.
    sections() {
      var out = [];
      var items = document.getElementById('main-cart-items');
      var footer = document.getElementById('main-cart-footer');
      if (items) out.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
      if (footer) out.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
      // Deliberately NOT the cart-icon-bubble section: deboer-header.liquid
      // renders its own "Cart · n" text on #cart-icon-bubble, not Dawn's
      // sections/cart-icon-bubble.liquid icon, so re-rendering that section
      // into it would swap the header's text for an SVG. count() below moves
      // the number instead. (Dawn's own cart.js still does the section swap —
      // pre-existing, see the note in STORE-SYNC-CHECKLIST.md.)
      return out;
    }

    render(state, sections) {
      if (!state || !state.sections) return;
      sections.forEach((section) => {
        var host = document.getElementById(section.id);
        if (!host) return;
        var html = state.sections[section.section];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector(section.selector);
        var target = host.querySelector(section.selector) || host;
        if (source) target.innerHTML = source.innerHTML;
      });

      // deboer-cart.liquid puts the empty state behind .dcart--empty, which is
      // only ever set server-side; without this the cart looks half-alive when
      // removing the last wetsuit also removes the gift.
      var count = typeof state.item_count === 'number' ? state.item_count : null;
      if (count !== null) {
        var list = document.querySelector('cart-items.dcart');
        if (list) list.classList.toggle('dcart--empty', count === 0);
        var foot = document.getElementById('main-cart-footer');
        if (foot) foot.classList.toggle('is-empty', count === 0);
        // Header count, surgically — see the note in sections().
        document.querySelectorAll('.db-cart-n').forEach(function (n) {
          n.textContent = count;
        });
        var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
        if (bubble) bubble.textContent = count;
      }

      // Injected markup starts at opacity 0 until the reveal observer picks it
      // up — rehydrate the new nodes or the gift row renders invisible.
      if (window.deboer && window.deboer.init) window.deboer.init(document.getElementById('main-cart-items'));

      // Guards the publish below: our own event must not re-enter reconcile.
      this.rendering = true;
      setTimeout(() => {
        this.rendering = false;
      }, 0);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // 'cart-items' is Dawn's "the cart UI is already re-rendered" signal —
        // cart.js publishes it for exactly this reason after its own render,
        // and CartItems skips its refetch on it. Announcing ourselves as
        // 'deboer-gift' instead makes CartItems fire a second, concurrent
        // fetch that can land after ours and paint stale markup over it —
        // which is how a colour swap lost the row it had just redrawn.
        // Everything else (quantity rules, price-per-item) still hears it.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      }
    }

    announce(key) {
      var msg = (window.deboerStrings || {})[key];
      if (msg && window.deboer && window.deboer.announce) window.deboer.announce(msg);
    }

    track(name, payload) {
      if (window.deboer && window.deboer.track) {
        window.deboer.track(name, Object.assign({ campaign: this.campaign }, payload || {}));
      }
    }

    read(k) {
      try {
        return window.sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    }
    store(k, v) {
      try {
        window.sessionStorage.setItem(k, v);
      } catch (e) {}
    }
    forget(k) {
      try {
        window.sessionStorage.removeItem(k);
      } catch (e) {}
    }
  }

  if (!customElements.get('deboer-gift-engine')) {
    customElements.define('deboer-gift-engine', DeboerGiftEngine);
  }
})();
